import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AppProfileService } from 'src/app/services/profile.service';
import { faEdit } from '@fortawesome/free-solid-svg-icons';
import { ActivatedRoute, Router } from '@angular/router';
import { map } from 'rxjs';
import { ExtendedProfile } from 'src/app/common/ExtendedProfile';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { PagedList } from 'src/app/common/PagedList';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NonConfirmedUsersComponent } from '../non-confirmed-users/non-confirmed-users.component';
import { ApplicationPaths } from 'src/app/auth/api-authorization.constants';
import { GridPdfColumnsDirective } from '../grid/grid-pdf-columns.directive';
import { PdfPageComponent } from '../grid/pdf-page.component';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

/**
 * Which list a route shows (#199 merged the two components that rendered them):
 * - `users` — „Benutzer": a retailer's own users, with „Neuen Benutzer erstellen".
 * - `registrations` — „Registrierungen": every customer's registrations, for the carrier only
 *   (`carrierGuard`). A retailer used to get this page too, showing the same users as
 *   „Benutzer" without the create button.
 */
export type UserListMode = 'users' | 'registrations';

@Component({
    imports: [KENDO_GRID, RouterModule, FontAwesomeModule, NonConfirmedUsersComponent, GridPdfColumnsDirective, PdfPageComponent, GridNoRecordsComponent],
    selector: 'app-shop-user-list',
    templateUrl: './user-list.component.html',
    styleUrls: ['./user-list.component.css']
})
export class UserListComponent implements OnInit {
  profileService = inject(AppProfileService);
  private router = inject(Router);

  private destroyRef = inject(DestroyRef);

  // A field initialiser, not a constructor assignment: `list` below reads it while it is built.
  public readonly mode: UserListMode = inject(ActivatedRoute).snapshot.data['userListMode'] ?? 'users';
  faEdit = faEdit;
  public createAllowed: boolean = false;
  public profile: ExtendedProfile = null;
  public ApplicationPaths = ApplicationPaths;

  // #648: server-side paging/sorting; backend default order = created desc.
  public list = new PagedList<ExtendedProfile>({
    sortFields: { role: 'role', status: 'status' },
    errorMessage:
      this.mode === 'registrations'
        ? $localize`Die Liste der Registrierungen konnte nicht geladen werden.`
        : $localize`Die Benutzerliste konnte nicht geladen werden.`,
    fetch: (paging) => {
      if (!this.profile) return null;
      const page$ =
        this.mode === 'registrations'
          ? this.profileService.getForCarrier(paging)
          : this.profileService.getAllOfCustomer(paging);
      return page$.pipe(map((r) => ({ ...r, items: this.profileService.mapProfileDisplayNames(r.items) })));
    },
    // #648: gate on the filtered total, not the page length.
    onLoaded: (page) => (this.createAllowed = page.totalCount < 3),
  });

  ngOnInit() {
    this.profileService.currentProfile$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((p) => {
      this.profile = p;
      this.list.load();
    });
  }

  /** The page's audience: the carrier for registrations, a retailer for its own users. */
  public get mayView(): boolean {
    return this.mode === 'registrations' ? !!this.profile?.isCarrier : !!this.profile?.customer?.isRetailer;
  }

  public onEdit(userId: string) {
    const edit = this.mode === 'registrations' ? '/shop-registration-edit/' : '/shop-user-edit/';
    this.router.navigate([edit + userId]);
  }
}
